/**
 * Momentum Indicator
 * 
 * Replaces ExecutionDebtTimer with credible metrics:
 * - Momentum badge (stable/slowing/at_risk)
 * - Closing probability with trend
 * - Expected value (if available)
 * - Why this is #1 (reasons)
 */

import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  computeCloseProbability, 
  computeMomentum, 
  computeExpectedValue,
  computeProbabilityDelta,
  formatProbability,
  formatExpectedValue,
  type MomentumStatus,
  type ProbabilityTrend,
} from '@/lib/dealMetrics';
import { Lead } from '@/hooks/useLeads';
import { Deal } from '@/hooks/useDeals';
import type { EliteAction } from '@/core/decision-engine/eliteEngine';

interface MomentumIndicatorProps {
  action: EliteAction;
  className?: string;
}

const MOMENTUM_CONFIG: Record<MomentumStatus, { label: string; className: string; icon: typeof CheckCircle }> = {
  stable: {
    label: 'Stable',
    className: 'bg-success/10 border-success/30 text-success',
    icon: CheckCircle,
  },
  slowing: {
    label: 'Slowing',
    className: 'bg-warning/10 border-warning/30 text-warning',
    icon: Clock,
  },
  at_risk: {
    label: 'At Risk',
    className: 'bg-destructive/10 border-destructive/30 text-destructive',
    icon: AlertTriangle,
  },
};

const TREND_CONFIG: Record<ProbabilityTrend, { icon: typeof TrendingUp; label: string; className: string }> = {
  up: {
    icon: TrendingUp,
    label: 'Improving',
    className: 'text-success',
  },
  flat: {
    icon: Minus,
    label: 'Stable',
    className: 'text-muted-foreground',
  },
  down: {
    icon: TrendingDown,
    label: 'Decreasing',
    className: 'text-destructive',
  },
};

export function MomentumIndicator({ action, className }: MomentumIndicatorProps) {
  const deal = action.deal;
  const lead = action.lead;

  if (!deal || !lead) {
    return null;
  }

  const probability = computeCloseProbability(deal, lead);
  const momentum = computeMomentum(deal, lead);
  const expectedValue = computeExpectedValue(deal, probability.probability);
  const trend = computeProbabilityDelta(deal, lead);

  const MomentumIcon = MOMENTUM_CONFIG[momentum.status].icon;
  const TrendIcon = TREND_CONFIG[trend.trend].icon;

  return (
    <div className={cn("space-y-3", className)}>
      {/* Momentum Badge */}
      <div className="flex items-center gap-2">
        <MomentumIcon className="w-3.5 h-3.5" />
        <Badge 
          variant="outline" 
          className={cn("text-[10px] px-2 py-0.5", MOMENTUM_CONFIG[momentum.status].className)}
        >
          Momentum: {MOMENTUM_CONFIG[momentum.status].label}
        </Badge>
      </div>

      {/* Closing Probability with Trend */}
      <div className="flex items-center gap-2 text-xs">
        <TrendIcon className={cn("w-3.5 h-3.5", TREND_CONFIG[trend.trend].className)} />
        <span className="text-muted-foreground">Closing probability:</span>
        <span className="font-semibold text-foreground">
          {formatProbability(probability.probability)}
        </span>
        {probability.isEstimated && (
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-muted-foreground/30 text-muted-foreground">
            Estimated
          </Badge>
        )}
        <span className={cn("text-xs", TREND_CONFIG[trend.trend].className)}>
          {trend.label}
        </span>
      </div>

      {/* Expected Value (if available) */}
      {expectedValue !== null && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Expected value:</span>
          <span className="font-semibold text-primary">
            {formatExpectedValue(expectedValue)}
          </span>
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-muted-foreground/30 text-muted-foreground">
            Estimated
          </Badge>
        </div>
      )}

      {/* Why this is #1 */}
      {action.reasons.length > 0 && (
        <div className="pt-2 border-t border-border/50">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
            Why this is #1
          </p>
          <ul className="space-y-1">
            {action.reasons.slice(0, 3).map((reason, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-foreground">
                <span className="text-primary mt-0.5">•</span>
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

